import { defineStore } from 'pinia'
import { ref, reactive } from 'vue'
import router from '@/router'
import { formatMarketCap } from '@/composables/useCompanyMarketData'
// import { useToastStore } from '@/stores/toastStore'

import {
  createDraft,
  getDraft,
  updateDraft,
  submitRegistration,
  getIndustries,
  getCompanyIndustryOptions,
  getCompanyMarketCap,
  getLogoUploadUrl
} from '@/api/company/registration'
import type { CompanyIndustryAttributes, CompanyIndustryOption } from '@/types/company'

const LS_KEY = 'reg.company.draftId'

interface Industry {
  id: number
  name: string
}

export const useCompanyRegistrationStore = defineStore('companyRegistration', () => {
  // const toast = useToastStore()

  const currentStep = ref(0)
  const totalSteps = 5
  const isSaving = ref(false)
  const error = ref<string | null>(null)
  const logoError = ref<string | null>(null)
  const draftId = ref<string |null>(null)
  const isLoaded = ref(false)
  const industries = ref<Industry[] | null>(null)
  const companyIndustryOptions = ref<CompanyIndustryOption[]>([])

  const form = reactive({
    companyName: '',
    companyEmail: '',
    contactEmail: '',
    companyLogo: '',
    companyLogoType: '',
    industry: '',
    industrySubcategoryId: null as number | null,
    industrySubcategory: '',
    industryAttributes: {} as CompanyIndustryAttributes,
    website: '',
    headOffice: '',
    employeeSize: '',
    companySummary: '',
    keyPeople: [{ firstName: '', lastName: '', role: '', roleStartDate: '' }],
    commodities: [] as string[],
    keyProjects: [{ name: '', stage: '', description: '', activity_feed: [] as Array<{ date: string; text: string }> }],
    companyTicker: '',
    marketCap: '',
    isPrivateCompany: false,
    cashInBank: '',
    cashInBankUpdatedAt: null as string | null,
    cashRunway: '',
    fieldRoster: '',
    typicalHours: '',
    facilities: '',
    softwareUsed: [] as string[],
    firstName: '',
    lastName: '',
    jobTitle: '',
    phoneNumber: ''
  })

  const toSafeString = (value: unknown): string => (typeof value === 'string' ? value : '').trim()

  const splitLegacyName = (value: unknown) => {
    const fullName = toSafeString(value)
    if (!fullName) return { firstName: '', lastName: '' }
    const parts = fullName.split(/\s+/)
    const firstName = parts.shift() ?? ''
    const lastName = parts.join(' ')
    return { firstName, lastName }
  }

  const normalizeKeyPeople = (value: unknown) => {
    if (!Array.isArray(value) || value.length === 0) {
      return [{ firstName: '', lastName: '', role: '', roleStartDate: '' }]
    }

    return value.map((entry: any) => {
      const firstName = toSafeString(entry?.firstName)
      const lastName = toSafeString(entry?.lastName)
      const role = toSafeString(entry?.role ?? entry?.position)
      const roleStartDate = toSafeString(entry?.roleStartDate ?? entry?.role_start_date ?? entry?.startDate ?? entry?.start_date)

      if (firstName || lastName) {
        return { firstName, lastName, role, roleStartDate }
      }

      const legacy = splitLegacyName(entry?.name)
      return { firstName: legacy.firstName, lastName: legacy.lastName, role, roleStartDate }
    })
  }

  const normalizeKeyProjects = (value: unknown) => {
    if (!Array.isArray(value) || value.length === 0) {
      return [{ name: '', stage: '', description: '', activity_feed: [] }]
    }
    return value.map((entry: any) => ({
      ...entry,
      description: toSafeString(entry?.description),
      activity_feed: Array.isArray(entry?.activity_feed ?? entry?.activityFeed)
        ? (entry?.activity_feed ?? entry?.activityFeed)
            .map((activity: any) => ({
              date: toSafeString(activity?.date ?? activity?.released_at ?? activity?.releasedAt),
              text: toSafeString(activity?.text ?? activity?.update ?? activity?.description),
            }))
            .filter((activity: any) => activity.date || activity.text)
        : [],
    }))
  }

  const normalizeFormPatch = (data: Record<string, any>) => {
    const next = { ...data }
    if (Object.prototype.hasOwnProperty.call(next, 'keyPeople')) {
      next.keyPeople = normalizeKeyPeople(next.keyPeople)
    }
    if (Object.prototype.hasOwnProperty.call(next, 'keyProjects')) {
      next.keyProjects = normalizeKeyProjects(next.keyProjects)
    }
    return next
  }

  const assignForm = (data: Record<string, any>) => {
    Object.assign(form, normalizeFormPatch(data))
  }

  const resetForm = () => {
    assignForm({
      companyName: '',
      companyEmail: '',
      contactEmail: '',
      companyLogo: '',
      companyLogoType: '',
      industry: '',
      industrySubcategoryId: null as number | null,
      industrySubcategory: '',
      industryAttributes: {} as CompanyIndustryAttributes,
      website: '',
      headOffice: '',
      employeeSize: '',
      companySummary: '',
      keyPeople: [{ firstName: '', lastName: '', role: '', roleStartDate: '' }],
      commodities: [] as string[],
      keyProjects: [{ name: '', stage: '', description: '', activity_feed: [] }],
      companyTicker: '',
      marketCap: '',
      isPrivateCompany: false,
      cashInBank: '',
      cashInBankUpdatedAt: null as string | null,
      cashRunway: '',
      fieldRoster: '',
      typicalHours: '',
      facilities: '',
      softwareUsed: [] as string[],
      firstName: '',
      lastName: '',
      jobTitle: '',
      phoneNumber: ''
    })
  }

  const initDraft = async () => {
  isLoaded.value = false
  error.value = null

  const savedDraftId = localStorage.getItem(LS_KEY)

  if (!savedDraftId) {

    resetForm()
    currentStep.value = 0
    draftId.value = null


    // ✅ DEV-MODE PREFILL
    const randomId = Math.floor(1000 + Math.random() * 9000) // e.g. 4821
    console.warn(`⚡ Prefilling test data (DEV mode, ID ${randomId})`)
    if (process.env.NODE_ENV === 'development') {
      console.warn('⚡ Prefilling test data (DEV mode)')
      assignForm({
        companyName: `Test Company ${randomId}`,
        companyEmail: `testcompany${randomId}@example.com`,
        contactEmail: `contact${randomId}@example.com`,
        companyLogo: '',
        companyLogoType: '',
	        industry: '2',
	        industrySubcategoryId: null,
	        industrySubcategory: '',
	        website: 'https://example.com',
        headOffice: 'Sydney, NSW',
        employeeSize: '11-50',
        companySummary: 'This is a test company summary for local dev testing.',
        keyPeople: [{ firstName: 'Jane', lastName: 'Doe', role: 'CEO', roleStartDate: '2024-01-01' }],
        commodities: ['Gold'],
        keyProjects: [{ name: 'Exploration Project', stage: 'Exploration', description: '', activity_feed: [] }],
        companyTicker: 'TEST',
        marketCap: '$123.45M AUD',
        isPrivateCompany: false,
        cashInBank: '5000000',
        cashInBankUpdatedAt: null,
        cashRunway: '4',
        fieldRoster: '2 weeks on / 1 week off',
        typicalHours: '9am - 5pm',
        facilities: 'Office, Lab, Warehouse',
        softwareUsed: ['Microsoft 365', 'Slack'],
        firstName: 'John',
        lastName: 'Tester',
        jobTitle: 'QA Engineer',
        phoneNumber: '0400000000'
      })
    }

    isLoaded.value = true
    return
  }

  // ✅ Existing draft found
  try {
    const { data } = await getDraft(savedDraftId)

    if (data && data.data) {
      const parsedData = typeof data.data === 'string' ? JSON.parse(data.data) : data.data
      assignForm(parsedData)
      currentStep.value = data.step ?? 0
      draftId.value = savedDraftId
    } else {
      throw new Error('Invalid draft data')
    }
  } catch (e) {
    // Something went wrong — reset everything
    localStorage.removeItem(LS_KEY)
    resetForm()
    currentStep.value = 0
    draftId.value = null

    // ✅ DEV fallback if API fails
    if (process.env.NODE_ENV === 'development') {
      console.warn('⚡ Using fallback test data (API failed)')
      assignForm({
        companyName: 'Fallback Test Co',
        companyEmail: 'fallback@example.com',
        contactEmail: '',
        website: 'https://example.com'
      })
    }
  } finally {
    isLoaded.value = true
  }
}

  const startDraft = async () => {
    if (draftId.value) return // already has a draft
    isSaving.value = true

    const payload = {
      companyName: form.companyName,
      companyEmail: form.companyEmail
    }

    try {
      const { data } = await createDraft(payload)
      draftId.value = data.id
      localStorage.setItem(LS_KEY, data.id)
    } catch (err) {
      console.log('Failed to create draft', err)
      error.value = 'Unable to create registration draft'
    } finally {
      isSaving.value = false
    }
  }

  const fetchIndustries = () => {
    getIndustries()
    .then(({ data }) => {
      industries.value = data
    })
    .catch((err) => {
      console.error('Failed to fetch industries', err)
    })
  }

  const fetchCompanyIndustryOptions = () => {
    getCompanyIndustryOptions()
    .then(({ data }) => {
      companyIndustryOptions.value = Array.isArray(data?.data) ? data.data : []
    })
    .catch((err) => {
      console.error('Failed to fetch company industry options', err)
    })
  }

  const updateForm = (data: Record<string, any>) => {
    assignForm(data)
  }

  const nextStep = async () => {
    // Create draft if finished first step
    if (currentStep.value === 0 && !draftId.value) {
      await startDraft()
    }

    if (currentStep.value < totalSteps - 1) {
      currentStep.value++
      autoSave()
      window.scrollTo({ top: 0, behavior: 'smooth' })
    }
  }

  const prevStep = () => {
    if (currentStep.value > 0) {
      currentStep.value--
      autoSave()
      window.scrollTo({ top: 0, behavior: 'smooth' })
    }
  }

  const autoSave = async () => {
    if (!draftId.value) return

    try {
      updateDraft(draftId.value, {
        data: form
      })
    } catch (err) {
      console.log('Auto-save failed (non-blocking)')
    }
  }

  const submit = async (payload: { password: string; confirmPassword: string}) => {
    isSaving.value = true
    error.value = null

    try {
      const finalPayload = {
        ...form,
        password: payload.password,
        confirmPassword: payload.confirmPassword,
        draftId: draftId.value
      }

      // Submit
      const { data } = await submitRegistration(finalPayload)

      if (data?.token) {
        localStorage.setItem('companyToken', data.token)
      }

      await router.push('/company/dashboard')
      .then(() => {
        localStorage.removeItem(LS_KEY)
        resetForm()
        draftId.value = null
        currentStep.value = 0
      })

      // toast.show('Registration successful!', 'success')
    } catch (err) {
      console.error('Submit failed')
      error.value = 'Submission failed, please try again'
    } finally {
      isSaving.value = false
    }
  }

  const fetchMarketCap = async (symbol: string) => {
    if (!symbol || symbol.trim().length < 2) return null

    try {
      const { data } = await getCompanyMarketCap(symbol.trim().toUpperCase())

      form.marketCap = formatMarketCap(data.market_cap)

      return data
    } catch (err: any) {
      if (err.response?.status === 404) {
        console.warn(`ASX symbol ${symbol} not found`)
        return { notFound: true }
      }

      console.error('Error fetching market cap:', err)
      return null
    }
  }

  const getPresignedLogoUrl = async (draftId: string, fileType: string) => {
    if (!draftId) throw new Error('Missing draft ID')
    if (!fileType) throw new Error('Missing file type')

    try {
      const { data } = await getLogoUploadUrl(draftId, fileType)
      return data // { uploadUrl, publicUrl, expiresIn }
    } catch (err: any) {
      console.error('Failed to get presigned S3 URL:', err)
      throw err
    }
  }

  return {
    currentStep,
    totalSteps,
    form,
    draftId,
	    industries,
	    companyIndustryOptions,
    isSaving,
    isLoaded,
    error,
    logoError,
    nextStep,
    prevStep,
    updateForm,
    autoSave,
    initDraft,
    startDraft,
	    fetchIndustries,
	    fetchCompanyIndustryOptions,
    submit,
    fetchMarketCap,
    getPresignedLogoUrl
  }
})
